import Api from '../Api'

// Receipt kind, chosen first on the public form (ABRA-388). Fixed once submitted.
export type ReceiptType = 'enrollment' | 'contract_extension'

export type SaleCondition = 'a_vista' | 'parcelado'
export type PaymentMethod = 'pix' | 'debit' | 'credit_machine' | 'credit_link'
export type ReceiptReference =
  'matricula' | 'primeira_parcela' | 'matricula_primeira_parcela' | 'total_contrato' | 'demais_parcelas'

// Online (aulas gravadas) path — ABRA-162.
export type SalePlatform = 'lia' | 'internal_sale'
export type OnlinePaymentMethod = 'credit' | 'boleto_parcelado' | 'recorrente' | 'pix'

export const RECEIPT_TYPE_LABELS: Record<ReceiptType, string> = {
  enrollment: 'Matrícula / Rematrícula',
  contract_extension: 'Extensão Contratual'
}

export const SALE_CONDITION_LABELS: Record<SaleCondition, string> = {
  a_vista: 'À Vista',
  parcelado: 'Parcelado'
}

export const RECEIPT_REFERENCE_LABELS: Record<ReceiptReference, string> = {
  matricula: 'Matrícula',
  primeira_parcela: '1ª Parcela',
  matricula_primeira_parcela: 'Matrícula + 1ª parcela',
  total_contrato: 'Total contrato',
  demais_parcelas: 'Demais Parcelas'
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  pix: 'Pix',
  debit: 'Débito',
  credit_machine: 'Crédito (Máquina)',
  credit_link: 'Crédito (Link)'
}

export const SALE_PLATFORM_LABELS: Record<SalePlatform, string> = {
  lia: 'Lia',
  internal_sale: 'Venda Interna'
}

export const ONLINE_PAYMENT_METHOD_LABELS: Record<OnlinePaymentMethod, string> = {
  credit: 'Crédito',
  boleto_parcelado: 'Boleto Parcelado',
  recorrente: 'Recorrente',
  pix: 'Pix'
}

// Label for a payment method from either path (presencial or online); '-' when absent.
export function paymentMethodLabel(value: string | null | undefined): string {
  if (!value) return '-'
  const presencial = PAYMENT_METHOD_LABELS as Record<string, string>
  const online = ONLINE_PAYMENT_METHOD_LABELS as Record<string, string>
  return presencial[value] ?? online[value] ?? value
}

export interface IConsultantOption {
  id: number
  name: string
  is_online_recorded_course: boolean
  is_online_live_course: boolean
}

// "Experiência do Aluno" panel user who sells contract extensions (ABRA-388).
export interface IUserOption {
  id: number
  name: string
}

export interface ICourseOption {
  id: number
  name: string
}

export interface ISalesReceiptCourse {
  id: number
  name: string
}

export interface ISalesReceipt {
  id: number
  receipt_type: ReceiptType
  commercial_consultant_id: number | null
  // Contract extensions link a panel user instead of a consultant (ABRA-388).
  user_id: number | null
  // Seller's name snapshot: the consultant's, or the user's on a contract extension.
  consultant_name: string
  // Presencial: uma ou mais matrículas por comprovante. Online: sempre 1 elemento
  // (o "Nº da Fatura / Pedido"). Coluna JSON compartilhada pelos dois fluxos.
  registration_number: string[]
  receipt_reference: ReceiptReference | null
  sale_condition: SaleCondition | null
  payment_method: PaymentMethod | OnlinePaymentMethod | null
  // Online path (ABRA-162): null on presencial receipts.
  sale_platform: SalePlatform | null
  student_email: string | null
  courses: ISalesReceiptCourse[] | null
  stone_id: string | null
  value: string | null
  receipt_path: string | null
  receipt_url: string | null
  prize: number | null
  prize_payment_date: string | null
  created_at: string
}

export interface ISalesReceiptExtraction {
  stone_id: string | null
  value: number | null
}

export interface IPrizeSummaryItem {
  consultant_name: string
  total_value: number
  total_prize: number
  quantity: number
}

export interface ISalesReceiptListResponse {
  data: ISalesReceipt[]
  recordsTotal: number
  recordsFiltered: number
  prize_summary?: IPrizeSummaryItem[]
}

export interface ISalesReceiptListParams {
  search?: string
  receipt_type?: string
  sale_condition?: string
  payment_method?: string
  receipt_reference?: string
  date?: { start?: string; end?: string }
  prize_payment_month?: string
  page?: number
  page_size?: number
}

export interface ISalesReceiptUpdatePayload {
  commercial_consultant_id: number
  registration_number: string[]
  receipt_reference: ReceiptReference
  sale_condition: SaleCondition
  payment_method: PaymentMethod
  stone_id: string | null
  value: number
}

// Contract extension: same fields as the presencial payload, linked to a user (ABRA-388).
export interface ISalesReceiptExtensionUpdatePayload extends Omit<
  ISalesReceiptUpdatePayload,
  'commercial_consultant_id'
> {
  user_id: number
}

export interface ISalesReceiptOnlineUpdatePayload {
  commercial_consultant_id: number
  registration_number: string[]
  student_email: string
  course_ids: number[]
  sale_platform: SalePlatform
  payment_method: OnlinePaymentMethod | null
  value: number
}

export class SalesReceiptService {
  async list(params: ISalesReceiptListParams): Promise<ISalesReceiptListResponse> {
    const { data } = await Api.get('/sales-receipts', {
      params: {
        search: params.search || '',
        receipt_type: params.receipt_type || '',
        sale_condition: params.sale_condition || '',
        payment_method: params.payment_method || '',
        receipt_reference: params.receipt_reference || '',
        date: params.date ? JSON.stringify(params.date) : '',
        prize_payment_month: params.prize_payment_month || '',
        page: params.page || 1,
        page_size: params.page_size || 25
      }
    })
    return data
  }

  async get(id: number): Promise<ISalesReceipt> {
    const { data } = await Api.get(`/sales-receipts/${id}`)
    return data
  }

  async update(
    id: number,
    payload: ISalesReceiptUpdatePayload | ISalesReceiptExtensionUpdatePayload | ISalesReceiptOnlineUpdatePayload
  ): Promise<ISalesReceipt> {
    const { data } = await Api.put(`/sales-receipts/${id}`, payload)
    return data
  }

  async updatePrizePaymentDate(id: number, prize_payment_date: string | null): Promise<ISalesReceipt> {
    const { data } = await Api.put(`/sales-receipts/${id}/prize-payment-date`, { prize_payment_date })
    return data
  }

  async delete(id: number): Promise<void> {
    await Api.delete(`/sales-receipts/${id}`)
  }

  // Public endpoints (used by the submission page, no auth required)

  async getConsultants(): Promise<IConsultantOption[]> {
    const { data } = await Api.get('/sales-receipts/consultants')
    return data
  }

  // "Experiência do Aluno" users for the contract extension path (ABRA-388).
  async getUsers(): Promise<IUserOption[]> {
    const { data } = await Api.get('/sales-receipts/users')
    return data
  }

  // Online ("aulas gravadas") courses for the online submission path (ABRA-162).
  async getCourses(): Promise<ICourseOption[]> {
    const { data } = await Api.get('/sales-receipts/courses')
    return data
  }

  async extract(receipt: File): Promise<ISalesReceiptExtraction> {
    const formData = new FormData()
    formData.append('receipt', receipt)
    const { data } = await Api.post('/sales-receipts/extract', formData)
    return data
  }

  async submit(formData: FormData): Promise<{ success: boolean; id: number }> {
    const { data } = await Api.post('/sales-receipts', formData)
    return data
  }
}
